// Metronome. Two click grids (settings → 박 기준, persisted):
//   fixed (default) — constant tempo fitted to the detected beats, one tempo per block when the song's
//                     tempo really changes (projectStore.tempoGrid ← fitFixedGrid)
//   detected        — every beat the chord grid shows, to hear whether beat detection lines up
// Each bar's first beat is accented.
//   overlay — clicks over the playing song (file: sample-accurate via the Web Audio clock;
//             YouTube: slaved to the IFrame clock, so a few ms of jitter is expected)
//   solo    — the metronome is its own PlaybackSource with a Web Audio clock: no song, the cursor
//             follows the clicks (controllers.activeSource() returns it while solo is on)
// Scheduling runs on its own timer (not rAF, which browsers throttle to ~1 fps in hidden/busy
// tabs) and books clicks well ahead on the Web Audio clock — "a tale of two clocks".
import * as Tone from "tone";
import type { PlaybackSource } from "./PlaybackSource";
import { useProjectStore } from "../state/projectStore";
import type { ChartMeasure } from "../types/domain";
import { usePlaybackStore, type MetroGrid } from "../state/playbackStore";

interface Click { t: number; accent: boolean }
/** where the song is right now, how far ahead it is safe to book clicks, and how much EARLIER than
 *  the beat each click must be issued so it is heard on the beat (output latency + user offset) */
export interface MetroPosition { raw: number; lookahead: number; lead: number }

const TICK_MS = 40;       // scheduler period
const JUMP = 0.25;        // position discontinuity (s) treated as a seek → reschedule

function rawCtx(): AudioContext {
  const ctx: any = Tone.getContext();
  return (ctx && ctx.rawContext) ? ctx.rawContext : ctx;
}
function outputLatency(): number {
  const raw: any = rawCtx();
  return (raw && (raw.outputLatency || raw.baseLatency)) || 0;
}
/** full Web Audio path delay (processing + device): a click booked at T is heard at T + this */
export function clickLatency(): number {
  const raw: any = rawCtx();
  return ((raw && raw.baseLatency) || 0) + ((raw && raw.outputLatency) || 0);
}

export class Metronome implements PlaybackSource {
  // ---- solo clock (same model as SongController) ----
  private baseT = 0;
  private ctxStart = 0;
  private running = false;
  private cursorComp = 0.1;

  // ---- click scheduling ----
  private clicks: Click[] = [];
  private clicksFor: ChartMeasure[] | null = null;
  private clicksGrid: MetroGrid | null = null;
  private next = -1;              // index of the next click to schedule; -1 = resync needed
  private lastRaw = 0;
  private lastCtx = 0;
  private pending: { node: OscillatorNode; when: number }[] = [];

  /** set by Controllers: the active source's position while the metronome should click, else null */
  position: () => MetroPosition | null = () => null;

  constructor() {
    if (typeof window !== "undefined") window.setInterval(() => this.tick(), TICK_MS);
  }

  setCursorComp(seconds: number): void { this.cursorComp = seconds; }

  private tick(): void {
    const p = this.position();
    if (!p) { if (this.pending.length || this.next >= 0) this.resync(); return; }   // paused/off: silence booked clicks
    this.schedule(p.raw, p.lookahead, p.lead);
  }

  get duration(): number {
    const ms = useProjectStore.getState().chartMeasures;
    return ms.length ? ms[ms.length - 1].endTime : 0;
  }

  /** song position as rendered by the audio clock (no latency/cursor compensation) */
  rawTime(): number {
    if (!this.running) return this.baseT;
    return this.baseT + Math.max(0, rawCtx().currentTime - this.ctxStart);
  }
  getPlaybackTime(): number {
    if (!this.running) return this.baseT;
    const lat = outputLatency() + this.cursorComp;
    return this.baseT + Math.max(0, rawCtx().currentTime - this.ctxStart - lat);
  }
  async play(): Promise<void> {
    await Tone.start();
    if (this.baseT >= this.duration - 0.05) this.baseT = 0;
    this.ctxStart = rawCtx().currentTime + 0.05;
    this.running = true;
    this.resync();
  }
  pause(): void {
    if (!this.running) return;
    this.baseT = this.getPlaybackTime();
    this.running = false;
    this.resync();
  }
  seek(t: number): void {
    this.baseT = Math.max(0, t);
    if (this.running) this.ctxStart = rawCtx().currentTime + 0.02;
    this.resync();
  }
  seekSilent(t: number): void { this.baseT = Math.max(0, t); this.resync(); }
  isPlaying(): boolean { return this.running; }

  /** drop scheduled-but-unplayed clicks and re-find the position on the next tick */
  resync(): void {
    const now = rawCtx().currentTime;
    for (const p of this.pending) if (p.when > now) { try { p.node.stop(); } catch { /* noop */ } }
    this.pending = [];
    this.next = -1;
  }

  /** book clicks for beats inside [rawPos, rawPos + lookahead] */
  private schedule(rawPos: number, lookahead: number, lead: number): void {
    const ctx = rawCtx();
    if (ctx.state !== "running") return;
    this.ensureClicks();
    const now = ctx.currentTime;
    // seek / pause-resume / clock hiccup → reschedule from the new position
    if (this.next >= 0 && Math.abs(rawPos - (this.lastRaw + (now - this.lastCtx))) > JUMP) this.resync();
    this.lastRaw = rawPos; this.lastCtx = now;
    if (this.next < 0) {
      this.next = 0;
      while (this.next < this.clicks.length && this.clicks[this.next].t < rawPos - 0.005) this.next++;
    }
    while (this.next < this.clicks.length && this.clicks[this.next].t <= rawPos + lookahead) {
      const c = this.clicks[this.next++];
      if (c.t < rawPos - 0.03) continue;                       // already past (frame stall) — skip, don't pile up
      this.click(Math.max(now, now + (c.t - rawPos) - lead), c.accent);
    }
    // stop the solo clock at the end of the grid
    if (this.running && rawPos >= this.duration) { this.pause(); this.baseT = 0; }
    const cutoff = now - 0.2;
    this.pending = this.pending.filter((p) => p.when > cutoff);
  }

  private ensureClicks(): void {
    const ms = useProjectStore.getState().chartMeasures;
    const grid = usePlaybackStore.getState().metroGrid;
    if (ms === this.clicksFor && grid === this.clicksGrid) return;
    this.clicksFor = ms; this.clicksGrid = grid;
    const fixed = grid === "fixed" ? useProjectStore.getState().tempoGrid : null;
    if (fixed) this.clicks = fixed.clicks;
    else {   // detected grid (or a chart too short to fit a tempo)
      this.clicks = [];
      for (const m of ms) m.beats.forEach((b, i) => this.clicks.push({ t: b.time, accent: i === 0 }));
    }
    this.resync();
  }

  private click(when: number, accent: boolean): void {
    const ctx = rawCtx();
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "square";
    osc.frequency.value = accent ? 1760 : 1175;
    const peak = accent ? 0.32 : 0.18;
    g.gain.setValueAtTime(0, when);
    g.gain.linearRampToValueAtTime(peak, when + 0.002);
    g.gain.exponentialRampToValueAtTime(0.0001, when + 0.045);
    osc.connect(g).connect(ctx.destination);
    osc.start(when);
    osc.stop(when + 0.06);
    this.pending.push({ node: osc, when });
  }
}
